import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HOUR_HEIGHT,
  appointmentBlock,
  blockStyle,
  blockedBlock,
  dayBounds,
  heightFor,
  hourMarks,
  layoutColumn,
  offsetFor,
  titleStyle,
  unavailableSpans,
  visitPositions,
  clientLabel,
  needsProvider,
} from "./diaryShared";
import { AppointmentHoverCard, StatusBadge, blockLabel, useHoverCard } from "./DiaryStatus";
import { formatTime, inZone, toClock } from "../../services/datetime";

/** `Appointment::TERMINAL_STATUSES`: nothing, a provider included, can follow. */
const FINISHED_STATUSES = ["completed", "cancelled", "no_show"];

/**
 * BIZ-12's day view, laid out by resource: one column per provider, plus a
 * "Branch" column only when something needs it — a branch with nobody active,
 * a booking with a service that has no provider yet (with a count badge), or
 * work of someone who has no column today.
 *
 * This is the view reception actually works from — "who is free at three" is a
 * question about columns, not about a list.
 *
 * ## Drag to reschedule
 *
 * Dragging a block moves it in 15-minute steps and, if it lands in another
 * provider's column, reassigns it. Nothing is written until the drop, and the
 * drop only reports the intent — the parent asks the API, which re-checks
 * availability and may still refuse. The grid never assumes it won.
 */
export default function DiaryDayView({
  date,
  timezone,
  providers,
  appointments,
  blocks,
  workingHours,
  hasActiveProviders = true,
  onSelect,
  onReschedule,
  onEmptyClick,
}) {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // No hover card mid-drag: it would chase the pointer across the grid.
  const hover = useHoverCard({ disabled: Boolean(drag) });

  /*
   * Finished history (completed, cancelled, no-show) whose providers have no
   * column — usually a booking nobody was ever assigned to — is left off the
   * day grid entirely: it can no longer be assigned, so it must not keep the
   * branch column open or stretch the day's hours. The agenda and the
   * appointments list still show it.
   */
  const gridAppointments = useMemo(() => {
    const drawn = new Set(providers.map((p) => p.id));
    return appointments.filter(
      (a) =>
        !FINISHED_STATUSES.includes(a.status) ||
        (a.services ?? []).some((s) => s.staff_id && drawn.has(s.staff_id))
    );
  }, [appointments, providers]);

  const bounds = useMemo(
    () => dayBounds([date], workingHours, gridAppointments, blocks, timezone),
    [date, workingHours, gridAppointments, blocks, timezone]
  );

  const marks = useMemo(() => hourMarks(bounds), [bounds]);
  const gridHeight = ((bounds.end - bounds.start) / 60) * HOUR_HEIGHT;

  // A column per provider, then one for anything nobody's column can hold:
  // services with no provider yet, and work of someone not drawn today.
  const allColumns = useMemo(
    () => [...providers, { id: "__branch__", display_name: t("diary.branchWide") }],
    [providers, t]
  );

  /**
   * Every positioned block, bucketed by the column it belongs in, plus
   * whether the branch column holds anything of its own.
   */
  const { byColumn, branchHasOwn, unassigned } = useMemo(() => {
    const buckets = Object.fromEntries(allColumns.map((c) => [c.id, []]));
    let own = false;
    let waiting = 0;

    gridAppointments.forEach((appointment) => {
      const block = appointmentBlock(appointment, date, timezone);
      if (!block) return;

      // A visit with two providers appears in both their columns — it is one
      // booking, but each person needs to see it in their own day.
      const targets = block.staffIds.filter((id) => buckets[id]);
      // A service with nobody on it also puts the booking in the branch
      // column, so it can be found and assigned.
      const open = needsProvider(appointment);
      if (open) waiting += 1;
      if (!targets.length || open) {
        own = true;
        buckets.__branch__.push(block);
      }
      targets.forEach((id) => buckets[id].push(block));
    });

    blocks.forEach((blocked) => {
      const block = blockedBlock(blocked, date, timezone);
      if (!block) return;

      if (blocked.is_branch_wide) {
        // A branch-wide block stops everybody, so it is drawn in every column.
        allColumns.forEach((c) => buckets[c.id].push(block));
      } else if (buckets[blocked.staff_id]) {
        buckets[blocked.staff_id].push(block);
      } else {
        own = true;
        buckets.__branch__.push(block);
      }
    });

    // Cancelled and no-show make room for live work rather than covering it.
    return {
      byColumn: Object.fromEntries(
        Object.entries(buckets).map(([id, items]) => [id, layoutColumn(items)])
      ),
      branchHasOwn: own,
      unassigned: waiting,
    };
  }, [allColumns, gridAppointments, blocks, date, timezone]);

  // The branch column is only drawn when it is needed: a branch with nobody
  // active to book, or something on this day that no provider's column holds.
  // Branch-wide blocks alone don't need it — they are in every column.
  const showBranch = !hasActiveProviders || providers.length === 0 || branchHasOwn;
  const columns = showBranch ? allColumns : providers;

  /*
   * Which blocks are one customer's visit.
   *
   * A grid block is two truncated lines at 30 minutes tall, so this is a
   * marker and a tooltip rather than a chip: reception sees at a glance that
   * two rows belong together, and hovering says how many. Read off the
   * appointments already loaded — no request, no coupling, and nothing here
   * treats a visit as a unit. Each block still drags, transitions and settles
   * entirely on its own.
   */
  const visits = useMemo(() => visitPositions(appointments), [appointments]);

  /** Pointer y -> branch-local minutes, snapped to a quarter hour. */
  const minutesAt = (clientY) => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return bounds.start;

    const minutes = bounds.start + ((clientY - rect.top) / HOUR_HEIGHT) * 60;
    return Math.round(minutes / 15) * 15;
  };

  const startDrag = (event, block, columnId) => {
    if (block.kind !== "appointment") return;
    // Only a booking that still occupies the diary can be moved, and not once
    // the API says its reschedule window has closed (90 min after the start).
    if (!["pending", "confirmed"].includes(block.appointment.status)) return;
    if (block.appointment.can_reschedule === false) return;

    event.preventDefault();

    const grabbedAt = minutesAt(event.clientY);

    setDrag({
      block,
      fromColumn: columnId,
      toColumn: columnId,
      grabOffset: grabbedAt - block.span.from,
      minutes: block.span.from,
    });
  };

  const onPointerMove = (event) => {
    if (!drag) return;

    const minutes = minutesAt(event.clientY) - drag.grabOffset;

    // Which column the pointer is over, by hit-testing the header cells.
    const under = document
      .elementsFromPoint(event.clientX, event.clientY)
      .find((el) => el.dataset?.columnId);

    setDrag({
      ...drag,
      minutes: Math.max(bounds.start, Math.min(minutes, bounds.end - 15)),
      toColumn: under?.dataset.columnId ?? drag.toColumn,
    });
  };

  const endDrag = () => {
    if (!drag) return;

    const moved = drag.minutes !== drag.block.span.from || drag.toColumn !== drag.fromColumn;
    const target = drag.toColumn === "__branch__" ? null : drag.toColumn;

    // Only report a real move, and never onto the branch-wide column — an
    // appointment has to belong to somebody.
    if (moved && target && onReschedule) {
      onReschedule({
        appointment: drag.block.appointment,
        date,
        time: toClock(drag.minutes),
        staffId: target,
      });
    }

    setDrag(null);
  };

  const nowSpan = (() => {
    const now = inZone(new Date().toISOString(), timezone);
    return now.date === date && now.minutes >= bounds.start && now.minutes <= bounds.end
      ? now.minutes
      : null;
  })();

  return (
    <div
      className="card overflow-hidden"
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Column headings */}
          <div className="flex border-b border-line bg-raised">
            <div className="w-14 flex-shrink-0" />
            {columns.map((provider) => (
              <div
                key={provider.id}
                data-column-id={provider.id}
                className="flex-1 border-s border-line px-2 py-2 text-center"
              >
                <p className="truncate text-xs font-medium text-ink">
                  {provider.display_name}
                </p>
                {provider.id === "__branch__" && unassigned > 0 && (
                  <span
                    className="mt-1 inline-block max-w-full truncate rounded-full bg-warning-wash px-2 py-0.5 text-caption font-medium text-warning-text"
                    title={t("diary.needsProviderHint")}
                  >
                    {t("diary.needsProvider", { count: unassigned })}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* The grid itself */}
          {/* `relative` is load-bearing: the now-line below positions against it. */}
          <div className="relative flex" ref={gridRef} style={{ height: gridHeight }}>
            {/* Hour axis */}
            <div className="relative w-14 flex-shrink-0">
              {marks.map((mark) => (
                <div
                  key={mark.minutes}
                  className="absolute -translate-y-1/2 pe-2 text-end text-caption text-ink-2"
                  dir="ltr"
                  style={{ top: offsetFor(mark.minutes, bounds.start), width: "100%" }}
                >
                  {mark.label}
                </div>
              ))}
            </div>

            {columns.map((provider) => {
              const gaps =
                provider.id === "__branch__" ? [] : unavailableSpans(provider, date, bounds);

              return (
                <div
                  key={provider.id}
                  data-column-id={provider.id}
                  className="relative flex-1 border-s border-line"
                  onDoubleClick={(e) => {
                    if (provider.id === "__branch__" || !onEmptyClick) return;
                    onEmptyClick({ date, time: toClock(minutesAt(e.clientY)), staffId: provider.id });
                  }}
                >
                  {/* Hour lines */}
                  {marks.map((mark) => (
                    <div
                      key={mark.minutes}
                      className="absolute w-full border-t border-line"
                      style={{ top: offsetFor(mark.minutes, bounds.start) }}
                    />
                  ))}

                  {/* Non-working background */}
                  {gaps.map((gap, i) => (
                    <div
                      key={i}
                      title={
                        gap.reason === "break"
                          ? gap.label || t("diary.break")
                          : t(`diary.${gap.reason}`)
                      }
                      className={`absolute w-full ${
                        gap.reason === "leave"
                          ? "bg-warning-wash"
                          : gap.reason === "break"
                            ? "bg-raised"
                            : "bg-raised"
                      }`}
                      style={{
                        top: offsetFor(gap.from, bounds.start),
                        height: heightFor(gap.to - gap.from),
                      }}
                    />
                  ))}

                  {/* Blocks */}
                  {(byColumn[provider.id] ?? []).map((block) => {
                    const dragging = drag?.block.id === block.id && drag?.fromColumn === provider.id;
                    const top = dragging
                      ? offsetFor(drag.minutes, bounds.start)
                      : offsetFor(block.span.from, bounds.start);

                    const width = `${block.width}%`;
                    const inset = `${block.left}%`;
                    const height = heightFor(block.span.to - block.span.from);

                    if (block.kind === "blocked") {
                      return (
                        <div
                          key={`b-${block.id}`}
                          title={block.blocked.reason || t("diary.blocked")}
                          className="absolute overflow-hidden rounded border border-line-strong bg-raised px-1 py-1 text-caption text-ink-2"
                          style={{ top, height, width, insetInlineStart: inset }}
                        >
                          <span className="block truncate">{block.blocked.reason || t("diary.blocked")}</span>
                        </div>
                      );
                    }

                    const a = block.appointment;
                    // Two lines only fit from about 34px; below that the
                    // second line would be cut in half, so it is dropped.
                    const roomy = height >= 34;

                    return (
                      <button
                        key={`a-${block.id}-${provider.id}`}
                        type="button"
                        aria-label={blockLabel(t, a)}
                        {...hover.bind(a)}
                        onPointerDown={(e) => {
                          hover.hide();
                          startDrag(e, block, provider.id);
                        }}
                        onClick={() => !drag && onSelect?.(a)}
                        className={`absolute overflow-hidden rounded px-1.5 py-0.5 text-start text-caption leading-tight
                          shadow-e1 transition-shadow hover:shadow-e2 focus-visible:ring-2
                          ${blockStyle(a.status)} ${dragging ? "opacity-70 shadow-lg" : ""}`}
                        style={{
                          top,
                          height,
                          width,
                          insetInlineStart: inset,
                          cursor: dragging ? "grabbing" : "pointer",
                          zIndex: dragging ? 30 : 10,
                        }}
                      >
                        {block.ghost ? (
                          // A narrow lane beside a live booking: the swatch
                          // and as much of the name as fits.
                          <span className="flex min-w-0 items-center gap-1">
                            <StatusBadge status={a.status} dotOnly />
                            <span className={`truncate font-medium ${titleStyle(a.status)}`}>
                              {clientLabel(t, a)}
                            </span>
                          </span>
                        ) : (
                          <>
                            <span className="flex min-w-0 items-center gap-1">
                              {visits.has(a.id) && (
                                <span
                                  aria-hidden="true"
                                  title={t("diary.visitOf", visits.get(a.id))}
                                  className="flex-shrink-0 opacity-70"
                                >
                                  ⚯
                                </span>
                              )}
                              <span className={`min-w-0 flex-1 truncate font-medium ${titleStyle(a.status)}`}>
                                {clientLabel(t, a)}
                              </span>
                              <StatusBadge status={a.status} compact className="max-w-[55%]" />
                            </span>
                            {roomy && (
                              <span className="block truncate opacity-80">
                                <span dir="ltr">{formatTime(a.local_starts_at)}</span>
                                {" · "}
                                {a.services?.[0]?.service?.name ?? ""}
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {/* Now line — only when the branch's today is what is on screen. */}
            {nowSpan !== null && (
              <div
                className="pointer-events-none absolute z-20 flex w-full items-center"
                style={{ top: offsetFor(nowSpan, bounds.start) }}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-danger" />
                <span className="h-px flex-1 bg-danger" />
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="border-t border-line px-3 py-2 text-caption text-ink-2">
        {t("diary.dragHint")}
      </p>

      <AppointmentHoverCard card={hover.card} />
    </div>
  );
}
